// Admin API service functions
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get token from localStorage
const getToken = () => localStorage.getItem('adminToken');

// Set token to localStorage
export const setToken = (token) => {
  localStorage.setItem('adminToken', token);
};

// Remove token from localStorage
export const removeToken = () => {
  localStorage.removeItem('adminToken');
};

// Check if user is authenticated
export const isAuthenticated = () => {
  return !!getToken();
};

// API request helper with auth
const authFetch = async (url, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(url, { ...options, headers });
  
  if (response.status === 401) {
    removeToken();
    window.location.href = '/admin/login';
    throw new Error('Unauthorized');
  }

  return response;
};

// Auth API
export const adminLogin = async (username, password) => {
  const response = await fetch(`${API}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password }),
  });

  if (!response.ok) {
    throw new Error('Login failed');
  }

  const data = await response.json();
  setToken(data.access_token);
  return data;
};

export const verifyAuth = async () => {
  const response = await authFetch(`${API}/auth/verify`);
  if (!response.ok) {
    throw new Error('Invalid token');
  }
  return response.json();
};

// Stats API
export const getStats = async () => {
  const response = await authFetch(`${API}/stats`);
  if (!response.ok) throw new Error('Failed to fetch stats');
  return response.json();
};

// Appointments API
export const getAppointments = async () => {
  const response = await authFetch(`${API}/appointments`);
  if (!response.ok) throw new Error('Failed to fetch appointments');
  return response.json();
};

export const updateAppointment = async (id, status) => {
  const response = await authFetch(`${API}/appointments/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
  if (!response.ok) throw new Error('Failed to update appointment');
  return response.json();
};

export const deleteAppointment = async (id) => {
  const response = await authFetch(`${API}/appointments/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete appointment');
  return response.json();
};

// Contact Messages API
export const getContactMessages = async () => {
  const response = await authFetch(`${API}/contact-messages`);
  if (!response.ok) throw new Error('Failed to fetch messages');
  return response.json();
};

export const updateContactMessage = async (id, read) => {
  const response = await authFetch(`${API}/contact-messages/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({ read }),
  });
  if (!response.ok) throw new Error('Failed to update message');
  return response.json();
};

export const deleteContactMessage = async (id) => {
  const response = await authFetch(`${API}/contact-messages/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete message');
  return response.json();
};

// Blog Posts API
export const getBlogPosts = async () => {
  const response = await fetch(`${API}/blog`);
  if (!response.ok) throw new Error('Failed to fetch blog posts');
  return response.json();
};

export const getBlogPost = async (id) => {
  const response = await fetch(`${API}/blog/${id}`);
  if (!response.ok) throw new Error('Failed to fetch blog post');
  return response.json();
};

export const createBlogPost = async (post) => {
  const response = await authFetch(`${API}/blog`, {
    method: 'POST',
    body: JSON.stringify(post),
  });
  if (!response.ok) throw new Error('Failed to create blog post');
  return response.json();
};

export const updateBlogPost = async (id, post) => {
  const response = await authFetch(`${API}/blog/${id}`, {
    method: 'PUT',
    body: JSON.stringify(post),
  });
  if (!response.ok) throw new Error('Failed to update blog post');
  return response.json();
};

export const deleteBlogPost = async (id) => {
  const response = await authFetch(`${API}/blog/${id}`, {
    method: 'DELETE',
  });
  if (!response.ok) throw new Error('Failed to delete blog post');
  return response.json();
};
